<template>
    <section class="payment-plans">
        <template v-if="hasReady && !loadError">
            <header class="payment-plans__header">
                <div>
                    <h1 class="payment-plans__title">Planos</h1>
                    <p class="payment-plans__subtitle">
                        Crie e personalize planos de assinatura e configure suas opções de renovação.
                    </p>
                </div>
                <button type="button" class="payment-plans__new" @click="openNewPlanModal">
                    <fa icon="fa-plus" />
                    Novo Plano
                </button>
            </header>

            <div v-if="!visiblePlans.length" class="payment-plans__empty">
                <h2>Nenhum plano cadastrado</h2>
                <p>Crie seu primeiro plano para disponibilizar assinaturas aos clientes.</p>
                <button type="button" class="payment-plans__new" @click="openNewPlanModal">
                    <fa icon="fa-plus" />
                    Criar primeiro plano
                </button>
            </div>

            <div v-else class="payment-plans__list">
                <article
                    class="plan-card"
                    v-for="plan in visiblePlans"
                    :key="plan.id"
                >
                    <div class="plan-card__icon" aria-hidden="true">
                        <fa :icon="planIcon(plan)" />
                    </div>
                    <div class="plan-card__content">
                        <div class="plan-card__name-row">
                            <h2 class="plan-card__name">{{ plan.name }}</h2>
                            <span v-if="plan.recommended == 1" class="plan-card__badge">
                                Recomendado
                            </span>
                        </div>
                        <div class="plan-card__price">
                            R$ {{ formatarNumero(plan.planValue) }}
                        </div>
                        <div class="plan-card__meta" v-if="planFrequency(plan)">
                            <span>{{ planFrequency(plan) }}</span>
                        </div>
                    </div>
                    <div class="plan-card__actions">
                        <v-tooltip text="Editar" location="top">
                            <template v-slot:activator="{ props }">
                                <button
                                    v-bind="props"
                                    type="button"
                                    class="plan-card__action"
                                    aria-label="Editar"
                                    @click="openEditPlanModal(plan)"
                                >
                                    <fa icon="fa-pencil" />
                                </button>
                            </template>
                        </v-tooltip>
                        <v-tooltip text="Excluir" location="top">
                            <template v-slot:activator="{ props }">
                                <button
                                    v-bind="props"
                                    type="button"
                                    class="plan-card__action plan-card__action--delete"
                                    aria-label="Excluir"
                                    @click="openDeleteDialog(plan)"
                                >
                                    <fa icon="fa-trash" />
                                </button>
                            </template>
                        </v-tooltip>
                    </div>
                </article>
            </div>

            <section class="payment-plans__trial">
                <div class="payment-plans__trial-head">
                    <div class="payment-plans__trial-lead">
                        <span class="payment-plans__trial-icon" aria-hidden="true">
                            <fa icon="fa-gift" />
                        </span>
                        <div>
                            <h2 class="payment-plans__trial-title">Período de teste gratuito</h2>
                            <p class="payment-plans__trial-text">
                                Defina se novos assinantes poderão experimentar a plataforma gratuitamente.
                            </p>
                        </div>
                    </div>
                    <div class="payment-plans__trial-status">
                        <span
                            class="payment-plans__trial-chip"
                            :class="{ 'payment-plans__trial-chip--on': isTrialActive }"
                        >
                            {{ statusTrial }}
                        </span>
                        <CustomToggle
                            v-model="isTrialActive"
                            @change="changeStatusTrial"
                        />
                    </div>
                </div>

                <div class="payment-plans__trial-body" v-if="isTrialActive">
                    <p class="payment-plans__trial-days">
                        Seus assinantes poderão experimentar seu conteúdo por
                        <strong>{{ trialRange[1] }} dias</strong>
                        gratuitamente.
                    </p>
                    <v-card class="cardTrial">
                        <v-card-text>
                            <v-slider
                                v-model="trialDays"
                                class="payment-plans__trial-slider"
                                :min="3"
                                :max="30"
                                :step="1"
                                thumb-label="always"
                                color="#4327c9"
                                track-color="#e7eaf2"
                                thumb-color="#4327c9"
                            >
                                <template v-slot:thumb-label="{ modelValue }">
                                    {{ modelValue }}
                                </template>
                            </v-slider>
                            <div class="payment-plans__trial-scale">
                                <span>3 dias</span>
                                <span>30 dias</span>
                            </div>
                        </v-card-text>
                    </v-card>
                    <p class="payment-plans__trial-warning">
                        <strong>Atenção:</strong> Alterar o período de teste afetará apenas novos assinantes.
                    </p>
                </div>

                <div class="payment-plans__trial-save">
                    <button type="button" class="payment-plans__save" @click="editTrial">
                        Salvar
                    </button>
                </div>
            </section>
        </template>

        <div v-else-if="hasReady && loadError" class="payment-plans__empty">
            <h2>Não foi possível carregar os planos</h2>
            <p>Tente novamente em instantes.</p>
            <button type="button" class="payment-plans__new" @click="getPlans">
                Tentar novamente
            </button>
        </div>

        <Loader :hasReady="hasReady" />
    </section>

    <v-dialog
        v-model="isNewPlanModalOpen"
        max-width="720"
        :persistent="newPlanFormSaving"
    >
        <div class="plan-modal" role="dialog" aria-labelledby="new-plan-title">
            <header class="plan-modal__header">
                <div>
                    <h2 id="new-plan-title" class="plan-modal__title">Novo Plano</h2>
                    <p class="plan-modal__subtitle">
                        Crie um plano e defina sua periodicidade de renovação.
                    </p>
                </div>
                <button
                    type="button"
                    class="plan-modal__close"
                    :disabled="newPlanFormSaving"
                    aria-label="Fechar"
                    @click="closeNewPlanModal"
                >
                    <fa icon="fa-xmark" />
                </button>
            </header>
            <div class="plan-modal__body">
                <NewPlanForm
                    ref="newPlanFormRef"
                    layout="modal"
                    hide-heading
                    @saving-change="newPlanFormSaving = $event"
                    @success="onNewPlanSuccess"
                    @cancel="closeNewPlanModal"
                />
            </div>
        </div>
    </v-dialog>

    <v-dialog
        v-model="isEditPlanModalOpen"
        max-width="720"
        :persistent="editPlanFormSaving"
    >
        <div
            v-if="editingPlanId"
            class="plan-modal"
            role="dialog"
            aria-labelledby="edit-plan-title"
        >
            <header class="plan-modal__header">
                <div>
                    <h2 id="edit-plan-title" class="plan-modal__title">Editar Plano</h2>
                    <p class="plan-modal__subtitle">
                        Atualize as informações do plano.
                    </p>
                </div>
                <button
                    type="button"
                    class="plan-modal__close"
                    :disabled="editPlanFormSaving"
                    aria-label="Fechar"
                    @click="closeEditPlanModal"
                >
                    <fa icon="fa-xmark" />
                </button>
            </header>
            <div class="plan-modal__body">
                <EditPlanForm
                    :key="editingPlanId"
                    :plan-id="editingPlanId"
                    layout="modal"
                    hide-heading
                    @saving-change="editPlanFormSaving = $event"
                    @success="onEditPlanSuccess"
                    @cancel="closeEditPlanModal"
                />
            </div>
        </div>
    </v-dialog>

    <v-dialog v-model="isDeleteDialogVisible" :max-width="600">
        <v-card class="modal-card">
            <v-card-title>
                <h3 class="mb-0">
                    Você tem certeza que deseja excluir esse plano?
                </h3>
            </v-card-title>
            <v-card-subtitle>
                Esta ação não poderá ser desfeita.
            </v-card-subtitle>
            <v-card-text>
                <p class="mb-1">
                    Nome: <strong>{{ planToDelete?.name }}</strong>
                </p>
                <p class="mb-1">
                    Valor: <strong>R$ {{ planToDelete ? formatarNumero(planToDelete.planValue) : '' }}</strong>
                </p>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    class="flex-grow-1 cancel-btn"
                    color="grey-darken-4"
                    variant="tonal"
                    @click="isDeleteDialogVisible = false"
                    >Cancelar</v-btn
                >
                <v-btn
                    class="flex-grow-1 text-white"
                    color="danger"
                    variant="flat"
                    :loading="isDeleting"
                    :disabled="isDeleting"
                    @click="confirmDelete"
                    >Excluir</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
    import Plan from '@/utils/Weflix/plan';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';
    import Loader from '@/components/Weflix/Loader';
    import CustomToggle from '@/components/Form/CustomToggle.vue';
    import NewPlanForm from '@/components/Weflix/Payment/NewPlanForm.vue';
    import EditPlanForm from '@/components/Weflix/Payment/EditPlanForm.vue';

    export default {
        components: {
            Loader,
            CustomToggle,
            NewPlanForm,
            EditPlanForm,
        },
        data() {
            return {
                statusTrial: 'Desativado',
                isTrialActive: false,
                trialRange: [3, 3],
                modelValue: 0,
                plans: [],
                hasReady: false,
                loadError: false,
                windowWidth: window.innerWidth,
                isMobileIcon: window.innerWidth < 600,
                isDeleteDialogVisible: false,
                planToDelete: null,
                isDeleting: false,
                isNewPlanModalOpen: false,
                newPlanFormSaving: false,
                isEditPlanModalOpen: false,
                editingPlanId: null,
                editPlanFormSaving: false,
            };
        },
        mounted() {
            window.addEventListener('resize', this.handleResize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.handleResize);
        },
        computed: {
            ...mapState(['token']),
            visiblePlans() {
                return (this.plans || []).filter((plan) => !this.isTrialPlan(plan));
            },
            trialDays: {
                get() {
                    const days = Number(this.trialRange[1]);
                    if (!Number.isFinite(days)) return 3;
                    return Math.min(30, Math.max(3, days));
                },
                set(value) {
                    const days = Math.min(30, Math.max(3, Number(value) || 3));
                    this.trialRange = [3, days];
                },
            },
        },
        methods: {
            openNewPlanModal() {
                this.isNewPlanModalOpen = true;
                this.$nextTick(() => {
                    this.$refs.newPlanFormRef?.resetForm?.();
                });
            },
            closeNewPlanModal() {
                if (this.newPlanFormSaving) return;
                this.isNewPlanModalOpen = false;
            },
            async onNewPlanSuccess() {
                this.isNewPlanModalOpen = false;
                await this.getPlans();
                this.notify('Plano criado com sucesso!', 'success');
                this.$nextTick(() => {
                    this.$refs.newPlanFormRef?.resetForm?.();
                });
            },
            openEditPlanModal(plan) {
                this.editingPlanId = plan.id;
                this.isEditPlanModalOpen = true;
            },
            closeEditPlanModal() {
                if (this.editPlanFormSaving) return;
                this.isEditPlanModalOpen = false;
                this.$nextTick(() => {
                    this.editingPlanId = null;
                });
            },
            async onEditPlanSuccess() {
                this.isEditPlanModalOpen = false;
                this.$nextTick(() => {
                    this.editingPlanId = null;
                });
                await this.getPlans();
                this.notify('Plano atualizado com sucesso!', 'success');
            },
            changeStatusTrial() {
                this.statusTrial = this.isTrialActive
                    ? 'Ativado'
                    : 'Desativado';
            },
            async getPlans() {
                try {
                    const response = await Plan.getPlans(this.token);
                    this.plans = response;
                    this.loadError = false;
                    this.hasReady = true;
                } catch (error) {
                    console.error(error);
                    this.loadError = true;
                    this.hasReady = true;
                }
            },
            isTrialPlan(plan) {
                const name = String(plan?.name || '').trim().toLowerCase();
                if (name === 'trial') return true;
                const value = Number(plan?.planValue);
                return plan?.planValue !== null
                    && plan?.planValue !== undefined
                    && plan?.planValue !== ''
                    && Number.isFinite(value)
                    && value === 0;
            },
            planFrequency(plan) {
                const labels = {
                    1: 'Mensal',
                    2: 'Semestral',
                    3: 'Trimestral',
                    4: 'Anual',
                };
                return labels[Number(plan.planType)] || '';
            },
            planIcon(plan) {
                if (plan.recommended == 1) return 'fa-crown';
                const icons = {
                    1: 'fa-calendar',
                    2: 'fa-calendar-check',
                    3: 'fa-rocket',
                    4: 'fa-star',
                };
                return icons[Number(plan.planType)] || 'fa-bookmark';
            },
            formatarNumero(numero) {
                let number = parseFloat(numero).toFixed(2);
                return number.replace('.', ',');
            },
            async editTrial() {
                this.hasReady = false;
                const payload = {
                    testPlan:
                        this.isTrialActive == false ? 0 : this.trialRange[1],
                };
                console.debug(payload, this.isTrialActive);
                try {
                    await Plan.editTrial(payload, this.token);
                } catch (error) {
                    console.error(error);
                } finally {
                    this.hasReady = true;
                }
            },
            async getTrial() {
                try {
                    const response = await Plan.getTrial(this.token);
                    const days = Number(response);
                    const normalized = Number.isFinite(days) ? days : 0;
                    this.isTrialActive = normalized > 0;
                    if (normalized > 0) {
                        this.trialRange = [3, normalized];
                    }
                    this.changeStatusTrial();
                } catch (error) {
                    console.error(error);
                }
            },
            openDeleteDialog(plan) {
                this.planToDelete = plan;
                this.isDeleteDialogVisible = true;
            },
            async confirmDelete() {
                if (!this.planToDelete) return;
                
                this.isDeleting = true;
                try {
                    await Plan.deletePlan(this.token, this.planToDelete.id);
                    this.isDeleteDialogVisible = false;
                    this.planToDelete = null;
                    // Recarrega a lista de planos
                    await this.getPlans();
                    this.notify('Plano excluído com sucesso!', 'success');
                } catch (error) {
                    console.error('Erro ao deletar plano:', error);
                    this.notify('Erro ao excluir plano. Tente novamente.', 'error');
                } finally {
                    this.isDeleting = false;
                }
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                });
            },
        },
        created() {
            this.getPlans();
            this.getTrial();
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
        },
    };
</script>

<style scoped>
    .payment-plans :deep(.cardTrial) {
        background: #fff;
        box-shadow: none;
        border: 1px solid #e7eaf2;
        border-radius: 12px;
        overflow: visible;
    }

    .payment-plans :deep(.cardTrial .v-card-text) {
        padding: 36px 16px 12px;
        overflow: visible;
    }

    .payment-plans :deep(.payment-plans__trial-slider) {
        margin: 0;
    }

    .payment-plans__trial-scale {
        display: flex;
        justify-content: space-between;
        margin-top: 2px;
        font-size: 12px;
        color: #64748b;
    }

    .payment-plans :deep(.v-slider-track__fill) {
        background-color: #4327c9 !important;
    }

    .payment-plans :deep(.v-slider-thumb__surface) {
        background-color: #4327c9 !important;
    }

    .payment-plans :deep(.v-slider-thumb__label) {
        background-color: #4327c9 !important;
    }
</style>
